// Carrom physics — planck.js wrapper used both for the prototype's
// client-side feel (so dragging-and-releasing the striker actually
// produces a believable shot) and, in Slice 3, the server's
// authoritative simulation.
//
// External interface keeps the unit space normalised: callers pass in
// coins and a striker with x/y in [0, 1] and get the same back.
// Internally we scale up by SIM_SCALE because Box2D / planck is most
// numerically stable when dynamic bodies sit between 0.1 and 10
// world units.
//
// `simulateShot` is pure — given the same inputs it produces the same
// timeline, which is what makes server-authoritative replay safe.
import { World, Vec2, Circle, Edge, type Body } from 'planck';

// World scale relative to the unit board (1.0 = full side).
const SIM_SCALE = 10;

// Pocket-centre inset on the unit board. Pocket centres sit at
// (FRAME_M, FRAME_M) etc. — i.e., this distance from each edge.
// Aligned with the visible holes drawn into Board.png.
const FRAME_M = 0.0475;

// Sink-display dots — where a coin/striker is visually placed at
// the MOMENT it is sunk, so the player clearly sees WHICH pocket
// took it (rather than the fade happening at some random rim
// position determined by the trajectory).
//
// These dots are INDEPENDENT of the physics pocket centres above
// because the visible pocket holes drawn into Board.png may not
// sit at exactly the same coordinates as the physics centres.
// When the user verifies the green debug markers visually line up
// with the image's holes, these are the final values used both
// for the visualisation AND the post-sink teleport.
//
// Order: top-left, top-right, bottom-left, bottom-right.
// Coordinates are normalised [0, 1] board fractions.
export const SINK_DOTS: ReadonlyArray<readonly [number, number]> = [
  [0.05625, 0.05625],
  [0.94375, 0.05625],
  [0.05625, 0.94375],
  [0.94375, 0.94375],
];

// Wall inset — separate from FRAME_M because the playable rectangle
// (where walls live) is intentionally LARGER than the rectangle
// formed by pocket centres. With WALL_M < FRAME_M the pockets sit
// INSIDE the walled play area, giving coins clear access to the
// pocket without being blocked by the wall before they get there.
// Reducing this widens the play area further; increasing it
// shrinks it. Tunable independently of pocket positions.
const WALL_M = 0.0475;

// Object radii match CARROM_BUILD_PLAN.md §11. These also drive the
// CSS sizing in carrom.css, so any change should be mirrored there.
const COIN_R = 0.022;
const STRIKER_R = 0.030;
const POCKET_R = 0.050;

// Pocket fall threshold (NORMALISED). A coin/striker is sunk only
// when its CENTRE crosses within this radius of a pocket centre —
// which is how a real carrom hole works (the puck has to actually
// be over the hole, not merely touching its rim). Earlier versions
// used a Box2D sensor at radius POCKET_R which fired contact when
// edge-of-coin met edge-of-sensor: that meant a coin sunk when its
// CENTRE was at distance POCKET_R + COIN_R = 0.072 from the pocket
// — way outside the visible hole — producing the "magnetic pull"
// effect the user was seeing.
//
// Effective sink distance — coin sinks when centre is this close
// to a pocket. Restored to the original sensor-equivalent value
// (POCKET_R + COIN_R = 0.072) so things actually go in the way
// they used to. The swept post-step check still prevents
// tunneling and the previous "magnetic pull" feel was a separate
// issue caused by sensor edge-of-circle detection, not by the
// numeric value itself.
let POCKET_FALL_RADIUS = 0.055404;

// Maximum speed at which a coin / striker can sink. Anything
// faster than this on entry to a pocket gets bounced off the rim
// instead — modelling a real wooden carrom hole, where a coin
// with too much momentum just skips across the hole and continues
// on its trajectory (slightly redirected). In normalised units
// (board-widths per second). Lower = more shots bounce; higher =
// only the hardest shots bounce.
let MAX_SINK_SPEED = 3.5;

// How much energy the rim absorbs when it rejects a too-fast
// coin. 0 = the radial component dies completely (coin stops
// dead radially). 1 = fully elastic bounce (loses no energy).
// 0.55 ≈ a slightly damped bounce, similar to a coin on a
// wooden carrom hole rim. Tangential velocity is preserved so
// the coin still skims along the rim.
let RIM_RESTITUTION = 0.55;

// Tunables — Slice 9 will move these into the admin-editable
// `CARROM_GAME_KEYS` so the feel can be live-tuned without redeploys.
// Tuned so a striker hitting a cluster actually feels like a striker
// hitting a cluster — energy gets absorbed in collisions, the felt
// drags coins down quickly, walls don't supercharge bounces. Slice 9
// will move these into admin-tunable `CARROM_GAME_KEYS` for live
// tuning without redeploys.
//
// `MAX_STRIKER_SPEED` stays at 7.5 because the striker speed itself
// felt right; the issue was downstream chaos. Reducing restitution
// and increasing damping fixes the chaos without slowing the shot.
let MAX_STRIKER_SPEED      = 6.00;
let COIN_LINEAR_DAMPING    = 4.00;
let COIN_ANGULAR_DAMPING   = 5.00;
let COIN_RESTITUTION       = 0.42;
let COIN_FRICTION          = 0.28;
let STRIKER_LINEAR_DAMPING = 4.60;
let STRIKER_RESTITUTION    = 0.50;
let WALL_RESTITUTION       = 0.42;
const SLEEP_THRESH           = 0.04;
const MAX_SIM_SECONDS        = 5.0;
const STEP                   = 1 / 60;

export type SimCoin = {
  id: number;
  color: 'white' | 'black' | 'queen';
  x: number;
  y: number;
  sunk: boolean;
};
export type SimStriker = { x: number; y: number; sunk: boolean };

export type Frame = { coins: SimCoin[]; striker: SimStriker };

/** Time-stamped event from the simulation, used by the playback to
 *  trigger sound effects at the right moment (collision clicks,
 *  pocket-drop thuds, etc.). `t` is seconds from the start of the
 *  shot. */
export type ShotEvent =
  | { type: 'collide-cc'; t: number; intensity: number } // coin-coin collision
  | { type: 'collide-sc'; t: number; intensity: number } // striker-coin collision
  | { type: 'sink';        t: number; coinId: number }   // any non-striker pocket entry
  | { type: 'striker-sunk'; t: number };                  // striker pocketed (foul)

export type ShotResult = {
  timeline: Frame[];
  finalCoins: SimCoin[];
  finalStriker: SimStriker;
  sunkCoinIds: number[];
  strikerSunk: boolean;
  /** Whether the striker made contact with at least one coin during
   *  the shot — used by the foul-detection step in Slice 3. */
  contactedAnyCoin: boolean;
  /** Total simulated wall-clock duration of the shot, in seconds. */
  durationSeconds: number;
  /** Sorted-by-`t` events the playback can fire sounds against. */
  events: ShotEvent[];
};

// Lightweight tagging — planck doesn't carry user-data through some
// fixture/body interfaces in older versions, so we attach our own.
type TaggedBody = Body & {
  __coinId?: number;
  __striker?: boolean;
  __pocket?: boolean;
};

export function simulateShot(
  coinsIn: SimCoin[],
  strikerIn: SimStriker,
  angle: number,
  power: number,
): ShotResult {
  const world = new World({ gravity: Vec2(0, 0) });

  // ─── Walls ───────────────────────────────────────────────────────
  // Walls sit at WALL_M inset (which is smaller than FRAME_M) so the
  // playable rectangle is bigger than the rectangle formed by pocket
  // centres. This gives coins clear access to each pocket without
  // hitting a wall just before reaching the hole.
  const wallBody = world.createBody();
  const M = WALL_M * SIM_SCALE;
  const N = (1 - WALL_M) * SIM_SCALE;
  const corners: Array<[number, number]> = [
    [M, M], [N, M], [N, N], [M, N],
  ];
  for (let i = 0; i < 4; i++) {
    const [ax, ay] = corners[i];
    const [bx, by] = corners[(i + 1) % 4];
    wallBody.createFixture({
      shape: new Edge(Vec2(ax, ay), Vec2(bx, by)),
      friction: 0.05,
      restitution: WALL_RESTITUTION,
    });
  }

  // ─── Pocket positions (no sensor fixtures!) ──────────────────────
  // Sensor fixtures used to live here. They were removed because:
  //   1) Box2D sensor contacts fire when EDGE meets EDGE, which
  //      means a coin sinks when its CENTRE is still POCKET_R +
  //      COIN_R outside the visible hole — the "magnetic pull"
  //      bug the user reported.
  //   2) Sinking is now decided post-step by SWEPT distance from
  //      each body's previous-to-current segment to each pocket
  //      centre. This is more accurate AND handles fast bodies
  //      that would otherwise tunnel through a small sensor in
  //      one step.
  const pocketCorners: Array<[number, number]> = [
    [FRAME_M,         FRAME_M],
    [1 - FRAME_M,     FRAME_M],
    [FRAME_M,         1 - FRAME_M],
    [1 - FRAME_M,     1 - FRAME_M],
  ];
  // Pre-scaled pocket centres for the post-step distance check.
  const pocketsScaled: Array<[number, number]> = pocketCorners.map(
    ([px, py]) => [px * SIM_SCALE, py * SIM_SCALE]
  );
  const pocketFallRadiusScaled = POCKET_FALL_RADIUS * SIM_SCALE;

  // ─── Coin bodies ─────────────────────────────────────────────────
  const coinIdToBody = new Map<number, TaggedBody>();
  for (const c of coinsIn) {
    if (c.sunk) continue;
    const body = world.createBody({
      type: 'dynamic',
      position: Vec2(c.x * SIM_SCALE, c.y * SIM_SCALE),
      linearDamping: COIN_LINEAR_DAMPING,
      angularDamping: COIN_ANGULAR_DAMPING,
      bullet: false,
    }) as TaggedBody;
    body.createFixture({
      shape: new Circle(COIN_R * SIM_SCALE),
      density: 1.0,
      friction: COIN_FRICTION,
      restitution: COIN_RESTITUTION,
    });
    body.__coinId = c.id;
    coinIdToBody.set(c.id, body);
  }

  // ─── Striker ─────────────────────────────────────────────────────
  const strikerBody = world.createBody({
    type: 'dynamic',
    position: Vec2(strikerIn.x * SIM_SCALE, strikerIn.y * SIM_SCALE),
    linearDamping: STRIKER_LINEAR_DAMPING,
    angularDamping: COIN_ANGULAR_DAMPING,
    bullet: true, // continuous collision so a fast striker can't tunnel a coin
  }) as TaggedBody;
  strikerBody.createFixture({
    shape: new Circle(STRIKER_R * SIM_SCALE),
    density: 1.5,
    friction: COIN_FRICTION,
    restitution: STRIKER_RESTITUTION,
  });
  strikerBody.__striker = true;

  const speed = MAX_STRIKER_SPEED * Math.max(0, Math.min(1, power)) * SIM_SCALE;
  strikerBody.setLinearVelocity(Vec2(Math.cos(angle) * speed, Math.sin(angle) * speed));

  // ─── Contact tracking ────────────────────────────────────────────
  //
  // CRITICAL: planck.js (like Box2D) forbids modifying body state
  // inside contact callbacks. Calling setActive(false) during
  // begin-contact corrupts the world's contact list and the very
  // next world.step() crashes inside updateContacts() with
  // "Cannot read properties of null (reading 'getBody')".
  //
  // So we just MARK the bodies here, then process them AFTER each
  // step() returns (see the `pendingDeactivation` queue drained in
  // the loop below).
  const sunkCoinIds = new Set<number>();
  let strikerSunk = false;
  let contactedAnyCoin = false;
  const pendingDeactivation: TaggedBody[] = [];
  const events: ShotEvent[] = [];
  // Time accumulator the contact callback reads from. Updated AFTER
  // each world.step() in the loop below, so contacts fired during
  // step N use the time at the start of that step — accurate enough
  // for sound-trigger purposes.
  let currentTime = 0;

  // Begin-contact tracks striker-vs-coin contacts (for the foul
  // rule) AND records every collision the playback can fire a
  // collision sound against. Pocket-fall detection is the swept
  // post-step check below, NOT the contact callback.
  world.on('begin-contact', (contact) => {
    const bA = contact.getFixtureA().getBody() as TaggedBody;
    const bB = contact.getFixtureB().getBody() as TaggedBody;
    const aCoin = typeof bA.__coinId === 'number';
    const bCoin = typeof bB.__coinId === 'number';
    const aStriker = !!bA.__striker;
    const bStriker = !!bB.__striker;

    if ((aStriker && bCoin) || (bStriker && aCoin)) {
      contactedAnyCoin = true;
      // Approximate intensity from the relative velocity at impact.
      // Higher relative speed → louder click.
      const sv = (aStriker ? bA : bB).getLinearVelocity();
      const cv = (aStriker ? bB : bA).getLinearVelocity();
      const dv = Math.hypot(sv.x - cv.x, sv.y - cv.y);
      const intensity = Math.min(1, dv / (MAX_STRIKER_SPEED * SIM_SCALE));
      events.push({ type: 'collide-sc', t: currentTime, intensity });
    } else if (aCoin && bCoin) {
      const va = bA.getLinearVelocity();
      const vb = bB.getLinearVelocity();
      const dv = Math.hypot(va.x - vb.x, va.y - vb.y);
      const intensity = Math.min(1, dv / (MAX_STRIKER_SPEED * SIM_SCALE));
      events.push({ type: 'collide-cc', t: currentTime, intensity });
    }
  });

  // Track each body's position at the START of the previous step so
  // we can compute the swept path it took during this step. Without
  // this a coin moving fast enough could tunnel past a small fall
  // zone in a single 1/60s step.
  const prevPositions = new Map<TaggedBody, { x: number; y: number }>();
  for (const body of coinIdToBody.values()) {
    const p = body.getPosition();
    prevPositions.set(body, { x: p.x, y: p.y });
  }
  {
    const p = strikerBody.getPosition();
    prevPositions.set(strikerBody, { x: p.x, y: p.y });
  }

  // ─── Step loop until rest or timeout ─────────────────────────────
  const maxFrames = Math.ceil(MAX_SIM_SECONDS / STEP);
  const timeline: Frame[] = [];

  for (let frame = 0; frame < maxFrames; frame++) {
    world.step(STEP, 8, 3);
    // Advance the simulation clock AFTER step so contacts fired
    // inside step() use the time at the START of the step.
    currentTime += STEP;

    // ─── Pocket fall detection (swept distance) ────────────────────
    // For every still-live body, take its position BEFORE this step
    // (`prev`) and AFTER this step (`curr`) and ask: did the line
    // segment prev→curr come within POCKET_FALL_RADIUS of any pocket
    // centre at any point? If yes, the body's centre crossed the
    // hole during this frame and it falls. Using the SEGMENT (not
    // just the endpoints) means a fast body that was on one side of
    // the hole at `prev` and on the other side at `curr` is still
    // detected — no tunneling.
    //
    // A body that runs out of velocity at the rim is detected by
    // segment-to-point distance > POCKET_FALL_RADIUS, so it stays
    // visually parked at the pocket edge with half its body over the
    // hole. That's the realistic teetering effect.
    const maxSinkSpeedScaled = MAX_SINK_SPEED * SIM_SCALE;
    const maxSinkSpeedSqScaled = maxSinkSpeedScaled * maxSinkSpeedScaled;

    const checkFall = (body: TaggedBody, isStriker: boolean): boolean => {
      const prev = prevPositions.get(body);
      const curr = body.getPosition();
      if (!prev) {
        prevPositions.set(body, { x: curr.x, y: curr.y });
        return false;
      }

      // Find which pocket (if any) the body's path during this step
      // came within fall radius of.
      let triggeredPocketX: number | null = null;
      let triggeredPocketY: number | null = null;
      for (const [px, py] of pocketsScaled) {
        if (segmentToPointDistance(prev.x, prev.y, curr.x, curr.y, px, py) < pocketFallRadiusScaled) {
          triggeredPocketX = px;
          triggeredPocketY = py;
          break;
        }
      }

      // Default: prev advances to curr at the end of this check.
      // The bounce branch overrides this to the pushed-back location.
      prevPositions.set(body, { x: curr.x, y: curr.y });

      if (triggeredPocketX === null || triggeredPocketY === null) {
        return false;
      }

      // ─── Speed gate: real-carrom "skip across the hole" rule ───
      // Read the body's current velocity. If it's moving faster than
      // MAX_SINK_SPEED, the rim rejects it and we apply a radial
      // bounce instead of sinking. This matches real-world physics:
      // a fast coin's momentum carries it across the pocket before
      // it can fall in.
      const v = body.getLinearVelocity();
      const speedSq = v.x * v.x + v.y * v.y;

      if (speedSq > maxSinkSpeedSqScaled) {
        // Reflect ONLY the radial velocity component. Tangential
        // velocity is preserved so the coin skims along the rim
        // and continues with its sideways motion.
        const dx = triggeredPocketX - curr.x;
        const dy = triggeredPocketY - curr.y;
        const distSq = dx * dx + dy * dy;
        if (distSq > 1e-8) {
          const dist = Math.sqrt(distSq);
          const nx = dx / dist; // unit vector pointing TOWARD pocket centre
          const ny = dy / dist;
          const radialDot = v.x * nx + v.y * ny; // component of v toward pocket
          if (radialDot > 0) {
            // Standard reflection along normal n with restitution e:
            //   v' = v − (1 + e) · (v · n) · n
            // For e = 1 it's a perfect bounce; e < 1 absorbs energy.
            const factor = (1 + RIM_RESTITUTION) * radialDot;
            body.setLinearVelocity(Vec2(
              v.x - factor * nx,
              v.y - factor * ny,
            ));
            // Push the body out to just past the fall radius so the
            // next frame's swept check starts from outside the
            // sink zone. Without this, the body's prev position
            // would still be inside the fall zone and the next
            // sweep would re-trigger a sink immediately.
            const targetDist = pocketFallRadiusScaled + COIN_R * SIM_SCALE * 0.5;
            if (dist < targetDist) {
              const pushOut = targetDist - dist;
              const newX = curr.x - nx * pushOut;
              const newY = curr.y - ny * pushOut;
              body.setPosition(Vec2(newX, newY));
              // Update prev to the pushed-back location so the
              // NEXT frame's segment starts cleanly from outside
              // the fall zone.
              prevPositions.set(body, { x: newX, y: newY });
            }
          }
        }
        // Did NOT sink — we bounced.
        return false;
      }

      // ─── Normal sink path ──────────────────────────────────────
      // Snap the body to the corresponding sink-display dot so the
      // sink animation plays at a fixed, image-aligned position
      // (rather than wherever in the fall zone the trajectory
      // happened to end up). Decide which corner from the
      // triggered pocket's quadrant.
      const halfBoardScaled = 0.5 * SIM_SCALE;
      const isLeft = triggeredPocketX < halfBoardScaled;
      const isTop  = triggeredPocketY < halfBoardScaled;
      const dotIdx = isTop ? (isLeft ? 0 : 1) : (isLeft ? 2 : 3);
      const [dotXNorm, dotYNorm] = SINK_DOTS[dotIdx];
      body.setPosition(Vec2(dotXNorm * SIM_SCALE, dotYNorm * SIM_SCALE));
      body.setLinearVelocity(Vec2(0, 0));
      body.setAngularVelocity(0);
      // The next swept check should start fresh from the dot
      // location, not from the pre-snap position.
      prevPositions.set(body, { x: dotXNorm * SIM_SCALE, y: dotYNorm * SIM_SCALE });

      if (isStriker) {
        if (!strikerSunk) {
          strikerSunk = true;
          pendingDeactivation.push(body);
          events.push({ type: 'striker-sunk', t: currentTime });
        }
      } else {
        const id = body.__coinId;
        if (typeof id === 'number' && !sunkCoinIds.has(id)) {
          sunkCoinIds.add(id);
          pendingDeactivation.push(body);
          events.push({ type: 'sink', t: currentTime, coinId: id });
        }
      }
      return true;
    };

    for (const body of coinIdToBody.values()) {
      if (!body.isActive()) continue;
      checkFall(body, false);
    }
    if (strikerBody.isActive() && !strikerSunk) {
      checkFall(strikerBody, true);
    }

    // Drain any bodies that just entered a pocket. Doing this here
    // (between steps) is safe — planck only forbids mutation inside
    // contact callbacks.
    while (pendingDeactivation.length > 0) {
      const body = pendingDeactivation.shift();
      if (!body) continue;
      try {
        body.setLinearVelocity(Vec2(0, 0));
        body.setAngularVelocity(0);
        body.setActive(false);
      } catch {
        // Defensive — if planck's internal state has already moved on,
        // don't take the whole shot down with us.
      }
    }

    const coinsFrame: SimCoin[] = coinsIn.map((c) => {
      if (c.sunk) return c;
      const body = coinIdToBody.get(c.id);
      if (!body) return c;
      const p = body.getPosition();
      return {
        ...c,
        x: p.x / SIM_SCALE,
        y: p.y / SIM_SCALE,
        sunk: sunkCoinIds.has(c.id),
      };
    });
    const sp = strikerBody.getPosition();
    const strikerFrame: SimStriker = {
      x: sp.x / SIM_SCALE,
      y: sp.y / SIM_SCALE,
      sunk: strikerSunk,
    };
    timeline.push({ coins: coinsFrame, striker: strikerFrame });

    // Rest check, sampled every 4 frames so we don't burn the loop on
    // the cheap dot products.
    if (frame > 8 && frame % 4 === 0) {
      let allRest = true;

      const sv = strikerBody.getLinearVelocity();
      if (
        !strikerSunk &&
        (Math.abs(sv.x) > SLEEP_THRESH * SIM_SCALE ||
         Math.abs(sv.y) > SLEEP_THRESH * SIM_SCALE)
      ) {
        allRest = false;
      }

      if (allRest) {
        for (const body of coinIdToBody.values()) {
          if (!body.isActive()) continue;
          const v = body.getLinearVelocity();
          if (
            Math.abs(v.x) > SLEEP_THRESH * SIM_SCALE ||
            Math.abs(v.y) > SLEEP_THRESH * SIM_SCALE
          ) {
            allRest = false;
            break;
          }
        }
      }

      if (allRest) break;
    }
  }

  const last = timeline[timeline.length - 1];
  // Sort events by time. begin-contact callbacks fire in the order
  // contacts are processed which already follows step order, but a
  // sort keeps the playback robust if any events were pushed
  // out-of-band (e.g. from the post-step fall checker).
  events.sort((a, b) => a.t - b.t);
  return {
    timeline,
    finalCoins: last.coins,
    finalStriker: last.striker,
    sunkCoinIds: Array.from(sunkCoinIds),
    strikerSunk,
    contactedAnyCoin,
    durationSeconds: timeline.length * STEP,
    events,
  };
}

// Re-exports so callers don't have to import from both places.
// POCKET_FALL_RADIUS + WALL_M are exported for the temporary
// debug overlay in Board.tsx so the user can SEE the fall zone
// AND the playable wall rectangle on top of Board.png.
export { COIN_R, STRIKER_R, POCKET_R, FRAME_M, WALL_M, POCKET_FALL_RADIUS };

// Closest distance from the line segment (ax,ay)→(bx,by) to the
// point (px,py). Used to test whether a body's path during one
// physics step came within `POCKET_FALL_RADIUS` of any pocket
// centre — i.e., did it cross the hole, even if briefly.
function segmentToPointDistance(
  ax: number, ay: number,
  bx: number, by: number,
  px: number, py: number,
): number {
  const dx = bx - ax;
  const dy = by - ay;
  const lenSq = dx * dx + dy * dy;
  if (lenSq < 1e-9) {
    // Degenerate segment — just the point distance.
    return Math.hypot(px - ax, py - ay);
  }
  let t = ((px - ax) * dx + (py - ay) * dy) / lenSq;
  if (t < 0) t = 0;
  else if (t > 1) t = 1;
  const cx = ax + t * dx;
  const cy = ay + t * dy;
  return Math.hypot(px - cx, py - cy);
}

// ─── Runtime config override ─────────────────────────────────────
// The hardcoded `let` constants near the top are boot-time defaults
// that mirror the server's CarromGame.* defaults. Page.tsx fetches
// /api/carrom/config on mount and calls setPhysicsConfig(...) with
// the response so the client preview matches the server-side
// simulator after any admin retuning — without a redeploy.
// Server omits a field → keep the boot default.
export type CarromPhysicsOverrides = {
  maxStrikerSpeed?: number;
  coinLinearDamping?: number;
  coinAngularDamping?: number;
  coinRestitution?: number;
  coinFriction?: number;
  strikerLinearDamping?: number;
  strikerRestitution?: number;
  wallRestitution?: number;
  pocketFallRadius?: number;
  maxSinkSpeed?: number;
  rimRestitution?: number;
};

export function setPhysicsConfig(o: CarromPhysicsOverrides): void {
  if (typeof o.maxStrikerSpeed       === 'number') MAX_STRIKER_SPEED      = o.maxStrikerSpeed;
  if (typeof o.coinLinearDamping     === 'number') COIN_LINEAR_DAMPING    = o.coinLinearDamping;
  if (typeof o.coinAngularDamping    === 'number') COIN_ANGULAR_DAMPING   = o.coinAngularDamping;
  if (typeof o.coinRestitution       === 'number') COIN_RESTITUTION       = o.coinRestitution;
  if (typeof o.coinFriction          === 'number') COIN_FRICTION          = o.coinFriction;
  if (typeof o.strikerLinearDamping  === 'number') STRIKER_LINEAR_DAMPING = o.strikerLinearDamping;
  if (typeof o.strikerRestitution    === 'number') STRIKER_RESTITUTION    = o.strikerRestitution;
  if (typeof o.wallRestitution       === 'number') WALL_RESTITUTION       = o.wallRestitution;
  if (typeof o.pocketFallRadius      === 'number') POCKET_FALL_RADIUS     = o.pocketFallRadius;
  if (typeof o.maxSinkSpeed          === 'number') MAX_SINK_SPEED         = o.maxSinkSpeed;
  if (typeof o.rimRestitution        === 'number') RIM_RESTITUTION        = o.rimRestitution;
}
